import { inject, Injectable } from '@angular/core';

import { Observable, Observer, Subscription, take } from 'rxjs';

import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import {
  SeatKind,
  StepDefinition,
  TellentClientDialogResult,
  UserInvitationFlow,
  UserInvitationStep,
  UserInvitationStepI,
} from '@core/tellent-types';
import { DialogRef, DialogService, DialogWidth } from '@core/user-interface-legacy';
import { useProtectedControlsCallback } from '@core/user-interface-routing-logic';

import { stepComponentMap } from './invitation-flow-component.map';
import { InvitationFlowStateService } from './invitation-flow-state.service';
import { UserInvitationFlowSelectionDialogComponent } from '../user-invitation-flow-selection-dialog/user-invitation-flow-selection-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class UserInvitationFlowFacade {
  private readonly dialogService = inject(DialogService);
  private readonly flowService = inject(InvitationFlowStateService);
  private readonly protectedControls = useProtectedControlsCallback();

  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  private readonly isCoreHrAvailable = this.currentUserFacade.seatByKind(SeatKind.CoreHR);

  private readonly stepComponentMap: Record<UserInvitationStep, UserInvitationStepI> =
    stepComponentMap;

  private activeDialogRef?: DialogRef;
  private activeFlowSub?: Subscription;

  public startNewMemberFlow(): void {
    this.startNewMemberFlowAsTellentClientCompatibleStream().subscribe();
  }

  public startNewMemberFlowAsTellentClientCompatibleStream(): Observable<any> {
    this.cancelCurrentFlow();

    const flowType = this.isCoreHrAvailable() ? undefined : UserInvitationFlow.AddMemberBasic;

    return new Observable(observer => {
      if (flowType) {
        this.initializeAndOpenFlow(flowType, observer);
        return;
      }

      this.activeDialogRef = this.dialogService.openDialog(
        UserInvitationFlowSelectionDialogComponent,
        {},
        {
          width: DialogWidth.Medium,
          containerOptions: { fullscreenOnMobile: true },
        },
      );

      this.activeFlowSub = this.activeDialogRef.afterClosed
        .pipe(take(1))
        .subscribe((selectedFlow: UserInvitationFlow | undefined) => {
          if (selectedFlow) {
            this.initializeAndOpenFlow(selectedFlow, observer);
          } else {
            observer?.next(undefined);
            observer?.complete();
          }
        });
    });
  }

  public startInviteExistingMemberFlow(activeDialogRef: DialogRef, memberGuid: string): void {
    activeDialogRef.close(UserInvitationFlow.InviteExistingMember);

    this.activeFlowSub = activeDialogRef.afterClosed
      .pipe(take(1))
      .subscribe((flowType: UserInvitationFlow | undefined) => {
        if (flowType) {
          this.initializeAndOpenFlow(flowType, undefined, { memberGuid });
        }
      });
  }

  public startInviteExistingMemberForPreonboardingFlow(
    activeDialogRef: DialogRef,
    memberGuid: string,
  ): void {
    activeDialogRef.close(UserInvitationFlow.InviteExistingMemberForPreonboarding);

    this.activeFlowSub = activeDialogRef.afterClosed
      .pipe(take(1))
      .subscribe((flowType: UserInvitationFlow | undefined) => {
        if (flowType) {
          this.initializeAndOpenFlow(flowType, undefined, { memberGuid });
        }
      });
  }

  private openStepDialog(
    stepDef: StepDefinition,
    observer?: Observer<TellentClientDialogResult<any>>,
    dialogData?: any,
  ): void {
    const stepToOpen = this.stepComponentMap[stepDef.step];
    const { dialogComponent, dialogConfig } = stepToOpen;

    if (!stepToOpen) {
      console.error(`No component registered for step: ${stepDef.step}`);
      observer?.next(undefined);
      observer?.complete();
      this.cancelCurrentFlow();
      return;
    }

    this.activeFlowSub?.unsubscribe();

    this.flowService.invitationData$.pipe(take(1)).subscribe(data => {
      this.activeDialogRef = this.dialogService.openDialog(
        dialogComponent,
        { ...data, ...stepDef, ...dialogData },
        { ...dialogConfig, closeGuard: () => this.protectedControls(() => true) },
      );

      if (this.activeDialogRef) {
        this.activeFlowSub = this.activeDialogRef.afterClosed.pipe(take(1)).subscribe(result => {
          if (result === undefined) {
            observer?.next(undefined);
            observer?.complete();
            this.activeDialogRef = undefined;
            this.activeFlowSub = undefined;
            return;
          }

          const nextStepDef = this.flowService.advanceFlow(result);

          if (nextStepDef) {
            this.openStepDialog(nextStepDef, observer);
          } else {
            this.handleFlowCompletion(observer);
          }
        });
      }
    });
  }

  private handleFlowCompletion(observer?: Observer<any>): void {
    this.flowService.finalizeFlow().subscribe({
      next: response => {
        observer?.next({ success: true, payload: response.payload });
        observer?.complete();
      },
      complete: () => {
        this.activeDialogRef = undefined;
        this.activeFlowSub = undefined;
      },
    });
  }

  private initializeAndOpenFlow(
    flowType: UserInvitationFlow,
    observer?: Observer<any>,
    dialogData?: any,
  ): void {
    const firstStep = this.flowService.initializeFlow(flowType);
    if (firstStep) {
      this.openStepDialog(firstStep, observer, dialogData);
    } else {
      observer?.next({ success: false, payload: 0 });
      observer?.complete();
    }
  }

  private cancelCurrentFlow() {
    if (this.activeDialogRef) {
      this.activeDialogRef.close();
    }
    if (this.activeFlowSub) {
      this.activeFlowSub.unsubscribe();
    }
  }
}
